import {
  ACTION,
  Alert,
  AlertVariants,
  Button,
  Dialog,
  Flex,
  FormControlSizes,
  HedgeCommandEnum,
  HedgeCommandStatusEnum,
  HedgeControlStatusEnum,
  HelpIcon,
  HStack,
  IconName,
  ModeEnum,
  NotificationVariants,
  Text,
  Tooltip,
  useDisclosure,
  type IHedgeRule,
  type ToastProps,
} from '@talos/kyoko';
import { useCallback } from 'react';
import { useHedgeRuleCommandMutation } from '../../containers/Trading/Markets/PositionAutoHedgingRules/queries';
import { useFeatureFlag, useRoleAuth } from '../../hooks';
import type { IHedgePositionStatusRow } from '../../providers/HedgePositionStatusProvider';
import { normalizeHedgeCrossAssetRiskGroup } from '../AutoHedging/crossAssetRiskGroup';

export function PositionAutoHedgingStatus({
  hedgePositionStatus,
  hedgeRule,
  mode,
  addToast,
  hasFormChanged,
}: {
  hedgePositionStatus: IHedgePositionStatusRow;
  hedgeRule: IHedgeRule;
  mode: ModeEnum;
  addToast: (toast: ToastProps) => void;
  hasFormChanged: boolean;
}) {
  const ruleDisabled = mode === ModeEnum.Disabled;

  const { sendCommand, isPending } = useSendHedgeCommand({ hedgeRule, addToast });
  const openOrdersWillCancelWarningDialog = useDisclosure();

  const handleClickButton = useCallback(() => {
    const intent = ruleDisabled ? 'Enable' : 'Disable';
    if (intent === 'Disable') {
      if (
        hedgePositionStatus.HedgeControlStatus === HedgeControlStatusEnum.Hedging &&
        hedgePositionStatus.HedgeOrderID
      ) {
        openOrdersWillCancelWarningDialog.open();
      } else {
        sendCommand(HedgeCommandEnum.Kill);
      }
    } else {
      sendCommand(HedgeCommandEnum.Activate);
    }
  }, [
    hedgePositionStatus.HedgeControlStatus,
    hedgePositionStatus.HedgeOrderID,
    openOrdersWillCancelWarningDialog,
    ruleDisabled,
    sendCommand,
  ]);

  const { isAuthorized } = useRoleAuth();

  const disallowEnablingRule = hasFormChanged && ruleDisabled;

  return (
    <>
      <Alert
        variant={ruleDisabled ? AlertVariants.Negative : AlertVariants.Positive}
        dismissable={false}
        alignItems="center"
        p="spacing8"
      >
        Rule is currently {ruleDisabled ? 'disabled' : 'enabled'}.
        <HStack ml="auto" gap="spacing4">
          {disallowEnablingRule && (
            <Tooltip tooltip="Please save or discard your changes before enabling the rule.">
              <HelpIcon />
            </Tooltip>
          )}
          <Button
            disabled={disallowEnablingRule || !isAuthorized(ACTION.EDIT_AUTOHEDGING)}
            data-testid="toggle-hedgerule"
            loading={isPending}
            onClick={handleClickButton}
            size={FormControlSizes.Small}
          >
            {ruleDisabled ? 'Enable' : 'Disable'}
          </Button>
        </HStack>
      </Alert>

      <Dialog
        {...openOrdersWillCancelWarningDialog}
        usePortal={true}
        portalID="drawer-portal"
        stretchButtons
        title="Order actively hedging"
        dataTestId="open-orders-will-cancel-warning-dialog"
        onConfirm={() => {
          sendCommand(HedgeCommandEnum.Kill);
        }}
      >
        There is an open order actively hedging this position. Disabling this rule will cancel the hedging order.
      </Dialog>
    </>
  );
}

export const TalosToolsPositionControlActions = ({
  hedgeRule,
  addToast,
}: {
  hedgeRule: IHedgeRule;
  addToast: (toast: ToastProps) => void;
}) => {
  const { isPending, sendCommand } = useSendHedgeCommand({ hedgeRule, addToast });

  return (
    <HStack py="spacing8" px="spacing16" alignItems="center" gap="spacing16" w="100%" justifyContent="space-between">
      <Text>Admin Tools:</Text>
      <Flex gap="spacing8" justifyContent="flex-end">
        <Button
          data-testid="synchronize-hedge-control"
          size={FormControlSizes.Small}
          loading={isPending}
          startIcon={IconName.Refresh}
          onClick={() => {
            sendCommand(HedgeCommandEnum.Synchronize);
          }}
        >
          Synchronize
        </Button>
        <Button
          data-testid="restart-hedge-control"
          size={FormControlSizes.Small}
          loading={isPending}
          startIcon={IconName.LightningBolt}
          onClick={() => {
            sendCommand(HedgeCommandEnum.Activate);
          }}
        >
          Restart Control
        </Button>
      </Flex>
    </HStack>
  );
};

const useSendHedgeCommand = ({
  hedgeRule,
  addToast,
}: {
  hedgeRule: IHedgeRule;
  addToast: (toast: ToastProps) => void;
}) => {
  const sendHedgeCommandMutation = useHedgeRuleCommandMutation();
  const { enableHedgeRuleCrossAssetRiskGroup } = useFeatureFlag();

  const { isPending } = sendHedgeCommandMutation;

  const sendCommand = useCallback(
    (command: (typeof HedgeCommandEnum)['Activate' | 'Kill' | 'Synchronize']) => {
      let verb: string = '';
      if (command === HedgeCommandEnum.Activate) {
        verb = 'enable';
      } else if (command === HedgeCommandEnum.Kill) {
        verb = 'disable';
      } else if (command === HedgeCommandEnum.Synchronize) {
        verb = 'synchronize';
      }

      sendHedgeCommandMutation
        .mutateAsync({
          Command: command,
          HedgeRuleID: hedgeRule.HedgeRuleID,
          ...(enableHedgeRuleCrossAssetRiskGroup
            ? { CrossAssetRiskGroup: normalizeHedgeCrossAssetRiskGroup(hedgeRule.CrossAssetRiskGroup) }
            : {}),
        })
        .then(res => {
          if (res.Status === HedgeCommandStatusEnum.Success) {
            addToast({
              text: `Successfully ${verb}d rule.`,
              variant: NotificationVariants.Positive,
            });
          } else {
            addToast({
              text: `Failed to ${verb} rule.\n${res.Error}`,
              variant: NotificationVariants.Negative,
            });
          }
        })
        .catch(() => {
          addToast({
            text: `Failed to ${verb} rule.`,
            variant: NotificationVariants.Negative,
          });
        });
    },
    [
      addToast,
      enableHedgeRuleCrossAssetRiskGroup,
      hedgeRule.CrossAssetRiskGroup,
      hedgeRule.HedgeRuleID,
      sendHedgeCommandMutation,
    ]
  );
  return { sendCommand, isPending };
};
